import { Alert, Box, SxProps, Theme } from '@mui/material';
import React, { useMemo } from 'react';
import { FORM_WIDGET_COMPONENTS } from '../../../../form/formConstants';
import FormWidget from '../../../../form/FormWidget';
import { useShallowMergeSxProps } from '../../../../hooks/useShallowMergeSxProps';
import I18n, { useI18n } from '../../../../i18n/I18n';
import { useReviewApprovalContext } from '../../hooks/useReviewApprovalContext';

export interface ApprovalActionProps {
    sx?: SxProps<Theme>;
}

export default function ApprovalAction({ sx }: ApprovalActionProps) {
    const mergedSx = useShallowMergeSxProps({ display: 'flex', flexDirection: 'column', gap: 2 }, sx);

    const actionInputLabelApprove = useI18n('approval.action.action.input.label.approve');
    const actionInputLabelReject = useI18n('approval.action.action.input.label.reject');
    const actionInputLabelRequestChange = useI18n('approval.action.action.input.label.requestChange');
    const commentInputPlaceholder = useI18n('approval.action.comment.input.placeholder');
    const commentInputHelperText = useI18n('approval.action.comment.input.helperText');

    const {
        isUserAllowedToViewApprovalActions,
        isUserAllowedToExecuteApprovalActions,
        approvalActionInput,
        isExecutingApprovalAction
    } = useReviewApprovalContext();

    const actionOptions = useMemo(() => {
        const options = [
            /**
             * @todo: Enable 'Request Change' action when the backend support is available.
             */
            { id: 3, label: actionInputLabelRequestChange, disabled: true },
            { id: 2, label: actionInputLabelReject, disabled: false },
            { id: 1, label: actionInputLabelApprove, disabled: false }
        ];

        return options.map((o) => ({
            ...o,
            disabled: o.disabled || !isUserAllowedToExecuteApprovalActions
        }));
    }, [
        actionInputLabelApprove,
        actionInputLabelReject,
        actionInputLabelRequestChange,
        isUserAllowedToExecuteApprovalActions
    ]);

    if (!isUserAllowedToViewApprovalActions) {
        return null;
    }

    const shouldDisableFormFields = !isUserAllowedToExecuteApprovalActions || isExecutingApprovalAction;

    return (
        <Box sx={mergedSx}>
            {!isUserAllowedToExecuteApprovalActions && (
                <Alert severity="info">
                    <I18n token="approval.waitingForPrecedingApprovers" />
                </Alert>
            )}

            <FormWidget
                component={FORM_WIDGET_COMPONENTS.chips}
                name="action"
                options={actionOptions}
                disabled={shouldDisableFormFields}
                containerSx={{ mb: 0 }}
            />

            {approvalActionInput?.action && (
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.textArea}
                    name="comment"
                    placeholder={commentInputPlaceholder}
                    minRows={4}
                    hideHelperText={false}
                    disabled={shouldDisableFormFields}
                    helperText={commentInputHelperText}
                />
            )}
        </Box>
    );
}
